import {
	NO_IMG_URL,
	API_URL,
	STICKER_API_URL,
	PREDICTED_RESULT_SIZE,
	RESULT_PAGE_SIZE,
	SEARCH_PAGE_MIN_SCORE
} from './constants.js';
import {createSuggestedQueries} from './utils/createsuggestedqueries.js';

export function fetchPriceRange(token, user, query, callback){
	var url = new URL(
		`${ API_URL }/${ encodeURIComponent(user) }/pricerange`,
		location.href
	);

	url.searchParams.set('q', query);

	var options = {
		method: 'GET',
		cache: 'no-store',
		mode: 'cors',
		headers: {
			'Accept': 'application/json',
			'Authorization': `Bearer ${ token }`
		},
        credentials: 'include'
	};

	fetch(url.href, options).then(function(res){
		if (!res.ok){
			throw new Error('HTTP error.');
		}

		return res.json();
	}).then(function(obj){
		obj.query = query;

		return obj;
	}).then(callback);
}

export function fetchPriceRangeFromState(state, callback){
	var {token, user} = state;
	var query = state.search.query;

	fetchPriceRange(token, user, query, (res) => {
		callback({
			query,
			min: res.aggregations.min_price.value,
			max: res.aggregations.max_price.value
		});
	});
}

// Todo: There maybe a better options for autocomplete / predicted results.
// https://www.elastic.co/guide/en/elasticsearch/reference/current/search-suggesters.html#completion-suggester
export function fetchPredictedResults(token, user, query, categories, callback){
	var url = new URL(
		`${ API_URL }/${ encodeURIComponent(user) }/predicted`,
		location.href
	);

	url.searchParams.set('q', query);
	url.searchParams.set('s', PREDICTED_RESULT_SIZE);

	categories.forEach(c => {
		url.searchParams.append('c[]', c);
	});

	var options = {
		method: 'GET',
		cache: 'no-store',
		mode: 'cors',
		headers: {
			'Accept': 'application/json',
			'Authorization': `Bearer ${ token }`
		},
        credentials: 'include'
	};

	fetch(url.href, options).then(function(res){
		if (!res.ok){
			throw new Error('HTTP error.');
		}

		return res.json();
	}).then(function(obj){
		obj.hits.hits = obj.hits.hits.map(function(r){
			var o = r._source;
			if (!o.image) o.image = NO_IMG_URL;
			return r;
		});

		obj.query = query;

		return obj;
	}).then(callback);
}

export function fetchResults(token, user, query, options, offset, callback){
	var {
		categories = [], 
		attributes = {}, 
		price: {
			min = null, 
			max = null
		} = {},
		minScore = 0
	} = options;

	var url = new URL(
		`${ API_URL }/${ encodeURIComponent(user) }/results`,
		location.href
	);

	url.searchParams.set('q', query);
	url.searchParams.set('o', offset);
	url.searchParams.set('s', RESULT_PAGE_SIZE);

	categories.forEach(c => {
		url.searchParams.append('c[]', c);
	});

	Object.keys(attributes).forEach(k => {
		attributes[k].forEach(v => {
			url.searchParams.append(`a[${k}][]`, v);
		});
	});

	if (min != null && max != null){
		url.searchParams.set('p[l]', min);
		url.searchParams.set('p[h]', max);
	}

	if (minScore > 0){
		url.searchParams.append('m', minScore);
	}

	var options = {
		method: 'GET',
		cache: 'no-store',
		mode: 'cors',
		headers: {
			'Accept': 'application/json',
			'Authorization': `Bearer ${ token }`
		},
        credentials: 'include'
	};

	fetch(url.href, options).then(function(res){
		if (res.status === 404){
			return res.json().then(function(obj){
				if (obj.error && obj.error.type === 'index_not_found_exception'){
					return Promise.resolve({
						hits: {
							total: {
								value: 0
							},
							hits: []
						}
					});
				}

				throw new Error('HTTP error.');
			});
		}

		if (!res.ok){
			throw new Error('HTTP error.');
		}

		return res.json();
	}).then(function(obj){
		obj.hits.hits = obj.hits.hits.map(function(r){
			var o = r._source;
			if (!o.image) o.image = NO_IMG_URL;
			return r;
		});
		
		obj.query = query;

		return obj;
	}).then(callback);
}

export function fetchResultsFromState(state, callback){
	var {
		isSearchPage,
		token, 
		user, 
		search: {
			query, 
			categories, 
			attributes, 
			price, 
			page
		}
	} = state;

	var options = {categories, attributes, price};

	const hasAttrs = Object.keys(attributes).length > 0;
	const hasPrice = price.min != null || price.max != null;

	if (isSearchPage && (hasAttrs || hasPrice)){
		options.minScore = SEARCH_PAGE_MIN_SCORE;
	}
	
	var offset = page === 0 
		? 0 
		: (page - 1) * RESULT_PAGE_SIZE;

	return fetchResults(token, user, query, options, offset, (res) => {
		callback({
			query,
			categories,
			attributes,
			price,
			page,
			res
		});
	})
}

export function getCachedPredictedResult(state, query){
	var s = '\u00B7'
	var cats = state.search.categories.slice().sort();
	var key = `${query.toLowerCase()}${s}${cats.join(s)}`;
	return state.suggestions.history.find(function(r){
		return r.query.toLowerCase() === `query.toLowerCase()`;
	});
}

export function getCachedPredictedResultOrFetch(store, state, query){
	var entry = getCachedPredictedResult(state, query);

	if (!entry){
		let {
			token,
			user,
			search: {
				categories
			}
		} = state;

		fetchPredictedResults(token, user, query, categories, function(res){
			store.dispatch({
				type: 'PREDICTED_RESULT_FETCHED',
				results: res,
				query,
				categories
			});
		});
		return state;
	}

	state.suggestions.queries = createSuggestedQueries(query, entry.suggest);
	state.suggestions.activeQuery = null;
	state.suggestions.items = entry.hits.hits.map(function(o){
		return o._source;
	});

	return state;
}

export function getSimilarResults(store, state){
	const {
		token,
		user,
		search: {
			query,
			categories,
		},
	} = state;

	fetchPredictedResults(token, user, query, categories, function(res){
		const suggest = res.suggest;

		const queries = createSuggestedQueries(query, suggest);
		
		if (queries.length === 0) return;
		
		const subQuery = queries[0].value || queries[0];

		fetchPredictedResults(token, user, subQuery, categories, function(res){
			store.dispatch({
				type: 'SIMILAR_RESULT_FETCHED',
				results: {
					suggest,
					hits: res.hits,
				},
				query,
				categories,
			});
		});
	});
	return state;
}

export function fetchFacets(token, attrNames, user, query, options, callback){
	var {
		categories = [], 
		attributes = {}, 
		price: {
			min = null, 
			max = null
		} = {},
		minScore = 0
	} = options;

	var url = new URL(
		`${ API_URL }/${ encodeURIComponent(user) }/facets`,
		location.href
	);

	url.searchParams.set('q', query);
	categories.forEach(c => {
		url.searchParams.append('c[]', c);
	});

	Object.keys(attributes).forEach(k => {
		attributes[k].forEach(v => {
			url.searchParams.append(`a[${k}][]`, v);
		});
	});

	attrNames.forEach(n => {
		url.searchParams.append('f[]', n);
	});

	if (minScore > 0){
		url.searchParams.append('m', minScore);
	}

	var options = {
		method: 'GET',
		cache: 'no-store',
		mode: 'cors',
		headers: {
			'Accept': 'application/json',
			'Authorization': `Bearer ${ token }`
		},
        credentials: 'include'
	};

	fetch(url.href, options).then(function(res){
		if (res.status === 404){
			return res.json().then(function(obj){
				if (obj.error && obj.error.type === 'index_not_found_exception'){
					throw new Error('Index not found');
				}

				throw new Error('HTTP error.');
			});
		}

		if (!res.ok){
			throw new Error('HTTP error.');
		}

		return res.json();
	}).then(function(obj){
		var aggs = {};
		if (!obj.aggregations) return aggs;
		Object.entries(obj.aggregations).forEach(([key, value]) => {
			var name = key.slice(5).toUpperCase();

			var buckets = (name == 'BRAND' || name == 'CONDITION')
				? value.attr_values.buckets
				: value.filterable_attr.filter_attr.attr_values.buckets;

			var values = buckets.map(b => ({
				name: b.key,
				count: b.doc_count
			}));

			aggs[name] = values;
		});
		return aggs;
	}).then(callback);
}

export function fetchFacetsFromState(state, callback){
	var {
		token, 
		user, 
		search: {
			query, 
			categories, 
			attributes, 
			price
		}
	} = state;

	var options = {categories, attributes, price};

	options.minScore = SEARCH_PAGE_MIN_SCORE;

	var attrNames = Object.keys(state.attributes);

	fetchFacets(token, attrNames, user, query, options, (res) => {
		callback({
			query,
			categories,
			attributes,
			price,
			res
		});
	});
}

export function fetchProductStickers(state, callback){
	let ids = state.search.items
		.filter(o => !Object.hasOwn(o, 'stickers'))
		.map(o => o.id)
		.map(s => s.slice(8, -1))
		.map(s => parseInt(s, 10));

	let url = new URL(STICKER_API_URL, location.href);

	ids.forEach(i => {
		url.searchParams.append('i', i);
	});

	fetch(url).then(res => {
		if (!res.ok){
			throw new Error('HTTP error.');
		}

		return res.json();
	}).then(o => o.data).then(callback);
}